import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { apiErrorMessage } from '@/shared/api-client';
import { useCreateRole } from './api';
import { PermissionPicker } from './PermissionPicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateRoleDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation('common');
  const createRole = useCreateRole();

  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName('');
    setPermissions(new Set());
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function toggle(code: string, checked: boolean) {
    setPermissions((current) => {
      const next = new Set(current);
      if (checked) next.add(code);
      else next.delete(code);
      return next;
    });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await createRole.mutateAsync({ name: name.trim(), permissions: [...permissions] });
      handleOpenChange(false);
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(t, 'roles', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('roles.create.title')}</DialogTitle>
            <DialogDescription>{t('roles.create.description')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="create-role-name">{t('roles.fields.name')}</Label>
            <Input
              id="create-role-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>{t('roles.fields.permissions')}</Label>
            <PermissionPicker selected={permissions} onToggle={toggle} />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={createRole.isPending}>
              {createRole.isPending ? t('roles.create.submitting') : t('roles.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
